'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';
import { useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRedux } from '../../_store/types';
import {
  createPointFromCoordinatesHomePage,
  findActivePoint,
  getUserLocation,
  shouldCreateNewPoint,
} from '../../_utils/location';
import Desktop from './desktop';
import Mobile from './mobile';
import { sessionActions } from '../../_store/session/session.slice';
import { IPoint } from '../../_store/common/types';

function AutoGeolocate() {
  const [geoloc, setGeoloc] = useState<[number, number]>([0, 0]);
  const [initalPoint, setInitalPoint] = useState<IPoint | null>(null);
  const { open, setOpen } = useDisclosure();
  const isDesktop = useIsDesktop();

  const { points } = useSelector((state: IRedux) => state.common);
  const { is_hall, permissions, order_type } = useSelector(
    (state: IRedux) => state.session,
  );

  const dispatch = useDispatch();

  const activePoint = useMemo(() => {
    if (!is_hall) return initalPoint || findActivePoint(points);
    return null;
  }, [is_hall, points, initalPoint]);

  const getGeolocation = () => {
    if (
      !is_hall &&
      order_type?.type !== 'self-pickup' &&
      permissions?.geolocation !== 'rejected'
    ) {
      getUserLocation()
        .then((location) => {
          if (location?.latitude) {
            const coords: [number, number] = [
              location.latitude,
              location.longitude,
            ];
            setGeoloc(coords);
          } else {
            dispatch(sessionActions.rejectPGL());
            console.warn('Geolocation success but no data');
          }
        })
        .catch((err) => {
          console.log('Failed to get location:', err);
          dispatch(sessionActions.rejectPGL());
        });
    }
  };

  useEffect(() => {
    if (is_hall === 'true') return;

    if (permissions.geolocation === 'accepted' && activePoint?.address) {
      setOpen(false);
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;

    const events: (keyof WindowEventMap)[] = [
      'click',
      'keydown',
      'scroll',
      'touchstart',
    ];

    const startTimer = () => {
      // Prevent multiple timers if effect re-runs quickly
      if (timer !== null) return;
      timer = setTimeout(() => {
        getGeolocation();
        setOpen(true);
      }, 5000);

      removeListeners();
    };

    const handleInteraction = () => {
      startTimer();
    };

    const addListeners = () => {
      events.forEach((event) =>
        window.addEventListener(event, handleInteraction, { passive: true }),
      );
    };

    const removeListeners = () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleInteraction),
      );
    };

    addListeners();

    return () => {
      if (timer) clearTimeout(timer);
      removeListeners();
    };
  }, [permissions.geolocation, activePoint?.address, is_hall]);

  useEffect(() => {
    if (shouldCreateNewPoint(geoloc, points, activePoint)) {
      createPointFromCoordinatesHomePage(geoloc).then((newPoint) => {
        if (newPoint) {
          setInitalPoint(newPoint);
        }
      });
    }
  }, [geoloc, points, activePoint]);

  if (is_hall === 'true') {
    return null;
  }
  if (order_type?.type === 'self-pickup') {
    return null;
  }

  if (isDesktop) {
    return <Desktop open={open} activePoint={activePoint} setOpen={setOpen} />;
  }

  return (
    <Mobile
      open={open}
      activePoint={activePoint}
      onClose={() => setOpen(false)}
    />
  );
}

export default AutoGeolocate;
